import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useIsRestoring } from '@tanstack/react-query';
import type { MeResponse, UserRole } from '@lk/shared';
import { LoadingState } from '../../components/States';
import { useMe } from './useAuth';

export interface RequireAuthProps {
  /** Кого пускати. Кілька ролей — для сторінок, які глядач бачить нарівні з батьком. */
  role?: UserRole | UserRole[];
  children: (me: MeResponse) => React.JSX.Element;
}

/**
 * Захист маршрутів. Якщо профілю немає (або сервер відповів 401) —
 * ведемо на вхід. Офлайн-черга при цьому лишається недоторканою.
 */
export function RequireAuth({ role, children }: RequireAuthProps): React.JSX.Element | null {
  const navigate = useNavigate();
  /**
   * Поки кеш піднімається з IndexedDB, запити ще не стартували, і `useMe`
   * виглядає як «немає користувача». Якщо не врахувати цю фазу, F5 посеред
   * сесії викидав би дитину на екран входу — рівно та поведінка, з якою
   * ми боремося. Тому відновлення кешу вважається завантаженням.
   */
  const isRestoring = useIsRestoring();
  const me = useMe();
  // `paused` = офлайн і жодних даних у кеші. Це вже не «чекаємо»,
  // а чесне «не знаємо, хто ти» — ведемо на вхід, не крутячи спінер вічно.
  const settling = isRestoring || (me.isPending && me.fetchStatus !== 'paused');

  const authenticated = Boolean(me.data?.user);
  const allowed = role === undefined ? null : Array.isArray(role) ? role : [role];
  const wrongRole = Boolean(allowed && me.data?.user && !allowed.includes(me.data.user.role));

  useEffect(() => {
    if (settling) return;
    if (!authenticated) {
      void navigate({ to: '/login', search: { expired: false } });
    } else if (wrongRole) {
      void navigate({ to: homeFor(me.data?.user.role) });
    }
  }, [settling, authenticated, wrongRole, navigate, me.data?.user.role]);

  if (settling) return <LoadingState />;
  if (!me.data?.user) return <LoadingState label="Перевіряємо, хто грає…" />;
  if (wrongRole) return <LoadingState label="Перекидаємо…" />;

  return children(me.data);
}

/** Домівка ролі: дитина — на арену, батько і глядач — у кабінет. */
export function homeFor(role: UserRole | undefined): '/parent' | '/arena' {
  return role === 'parent' || role === 'viewer' ? '/parent' : '/arena';
}

/**
 * Хто бачить статистику кабінету (зведення, навички, помилки). Гаманець,
 * налаштування і модерація лишаються з `role="parent"` — глядача звідти
 * поверне на зведення, а сервер однаково відмовить йому в будь-якій зміні.
 */
export const STATS_ROLES: UserRole[] = ['parent', 'viewer'];
